import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { EPISODE, HEALTH_PLAN, PLAN_TYPE } from 'constants/classifications';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { Classification } from 'models/flexible/Classification';
import { PERMISSIONS } from 'models/Profile';
import {
  adminClassificationsQueryKeys,
  adminIndexClassifications,
  updateClassification,
} from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import type { AdminClassificationIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import DeactivateIcon from 'svg/DeactivateIcon';
import PencilIcon from 'svg/PencilIcon';
import RestoreIcon from 'svg/RestoreIcon';

import ClassificationModal from './ClassificationModal';
import { classificationPageTitle, classificationTitle } from './helpers';

const CLASSIFICATION_INCLUDES = ['childClassifications', 'parentClassification'] as const;

type ClassificationRow = Classification<AdminClassificationIndexFields<(typeof CLASSIFICATION_INCLUDES)[number]>>;
function buildConfig(type: string): AdminListPageConfig<ClassificationRow> {
  const title = classificationTitle(type);
  const pageTitle = classificationPageTitle(type);

  const filters = [
    ...(type === PLAN_TYPE
      ? [
          {
            key: 'parentClassificationId',
            label: 'Plan',
            type: 'dropdown' as const,
            fetchConfig: {
              queryKey: ['adminClassifications', 'health_plan', 'filter'],
              fetchFn: adminIndexClassifications,
              params: { type: HEALTH_PLAN, archived: false, sortBy: 'name asc' },
            },
            getOptionLabel: (o: any) => o.name,
            getOptionValue: (o: any) => o.id,
          },
        ]
      : []),
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown' as const,
      fetchConfig: {
        queryKey: ['adminClients', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
  ];

  const columns = (() => {
    switch (type) {
      case HEALTH_PLAN:
        return [
          {
            id: 'name',
            header: 'Plan',
            render: (row: ClassificationRow) =>
              row.archived ? <ArchivedText>{row.name}</ArchivedText> : <strong>{row.name}</strong>,
          },
          {
            id: 'childClassifications',
            header: 'Associated Plan Types',
            sortable: false,
            render: (row: ClassificationRow) => (
              <BodySmall>{row.childClassifications?.map((c) => c.name).join(', ') || '—'}</BodySmall>
            ),
          },
        ];
      case PLAN_TYPE:
        return [
          {
            id: 'name',
            header: 'Name',
            render: (row: ClassificationRow) => <strong>{row.name}</strong>,
          },
          {
            id: 'parentClassification',
            header: 'Plan',
            render: (row: ClassificationRow) => <>{row.parentClassification?.name || '—'}</>,
          },
          {
            id: 'clientNames',
            header: 'Associated Clients',
            sortable: false,
          },
        ];
      case EPISODE:
      default:
        return [
          {
            id: 'name',
            header: 'Name',
            render: (row: ClassificationRow) => <strong>{row.name}</strong>,
          },
          {
            id: 'clientNames',
            header: 'Associated Clients',
            sortable: false,
          },
        ];
    }
  })();

  return {
    title: pageTitle,
    columnStorageKey: `admin.classifications.${type}`,
    queryKey: adminClassificationsQueryKeys.index({ type }),
    fetchFn: (params, signal) => adminIndexClassifications(params, signal, [...CLASSIFICATION_INCLUDES]),
    includes: CLASSIFICATION_INCLUDES,
    fetchParams: {
      defaultSort: 'name asc',
      extra: { type },
    },
    searchPlaceholder: 'Search...',
    showResultCount: true,

    addButton: {
      label: `Add ${title}`,
      permission: PERMISSIONS.adminClassificationCreate,
      onClick: (ctx) => ctx.openModal('edit', new Classification({ type })),
    },

    filters,
    columns,

    rowActions: [
      {
        label: 'Edit',
        icon: <PencilIcon width={18} height={18} />,
        permission: PERMISSIONS.adminClassificationEdit,
        ...(type === HEALTH_PLAN ? { visible: (row: ClassificationRow) => !row.archived } : {}),
        onClick: (row, { openModal }) => openModal('edit', row),
      },
      ...(type === HEALTH_PLAN
        ? [
            {
              label: 'Archive',
              icon: <DeactivateIcon width={18} height={18} color={colors.accentRed} />,
              danger: true,
              permission: PERMISSIONS.adminClassificationEdit,
              visible: (row: ClassificationRow) => !row.archived,
              onClick: (row: ClassificationRow, { openModal }: any) => openModal('archive', row),
            },
            {
              label: 'Unarchive',
              icon: <RestoreIcon width={18} height={18} />,
              permission: PERMISSIONS.adminClassificationEdit,
              visible: (row: ClassificationRow) => !!row.archived,
              onClick: (row: ClassificationRow, { openModal }: any) => openModal('unarchive', row),
            },
          ]
        : []),
    ],

    modals: {
      ...(type === HEALTH_PLAN
        ? {
            archive: {
              actionText: 'Archive',
              objType: 'plan',
              onConfirm: async (row: ClassificationRow) => {
                await updateClassification(row.id, { archived: true });
              },
            },
            unarchive: {
              actionText: 'Unarchive',
              objType: 'plan',
              onConfirm: async (row: ClassificationRow) => {
                await updateClassification(row.id, { archived: false });
              },
            },
          }
        : {}),
    },

    customModals: {
      edit: {
        render: (row, show, onClose, ctx) => (
          <ClassificationModal
            isOpen={show}
            classification={row}
            title={title}
            onCancel={onClose}
            onComplete={() => {
              onClose();
              ctx.refetch();
            }}
          />
        ),
      },
    },
  };
}

const ROUTE_TO_TYPE: Record<string, string> = {
  'episode-types': EPISODE,
  'plan-types': PLAN_TYPE,
  plans: HEALTH_PLAN,
};

export default function Classifications() {
  const location = useLocation();
  const routeSegment = location.pathname.split('/').pop()!;
  const type = ROUTE_TO_TYPE[routeSegment] ?? routeSegment;

  const config = useMemo(() => buildConfig(type), [type]);
  const state = useAdminListPage(config);

  return <AdminListPageRenderer config={config} state={state} />;
}

const ArchivedText = styled.span`
  font-weight: 700;
  color: var(--black-25);
`;
